// Distribución de la ficha de obra: pestañas de trabajo y tarjetas de campos.
//
// La ficha es para trabajar (editar datos), no un cuadro de mando: los KPI de cartera
// viven en Resumen, CAPEX, etc. Aquí hay tres pestañas con un volumen parecido (más la de
// Tecnología, más corta, que va aparte a propósito) y, dentro de cada una, tarjetas emparejadas por filas de dos con un "peso" parecido (el orden del
// array es el orden en la rejilla: 1-2, 3-4...). Una tarjeta con `wide: true` ocupa la
// fila entera. Los campos de texto largo (textarea) pesan más que un campo normal.
// Tarjetas por zonas (iluminación, suelos): rejilla de columnas pares y filas con sentido.
// `pairs`: campos que se leen juntos (proveedor -> tipo de cada zona) y van siempre uno al
// lado del otro. `rowBreaks`: campos que empiezan fila nueva (p. ej. la entrega a obra).
import { getCapexDeviation } from "./portfolio/portfolioShared";

export const DETAIL_TABS = [
    {
        id: "general",
        label: "General",
        sections: [
            { id: "identificacion", label: "Identificación", note: "Código, nombre, ubicación, tipo, campaña y estado", fields: ["codigoTienda", "tienda", "pais", "ciudad", "tipo", "campana", "estado"] },
            { id: "equipo", label: "Equipo", note: "Adjudicatarios y project managers", fields: ["consulting", "constructora", "studio", "pm", "pm2", "pm3", "pmi"] },
            { id: "planificacion", label: "Planificación y superficies", note: "Inicio, apertura, duración y metros", fields: ["inicioObra", "apertura", "dias", "supCons", "supVta"] },
            { id: "operativa", label: "Operativa de tienda", note: "Almacén, exposición comercial y cestas", fields: ["capacidadAlmacen", "mlConfeccion", "mlPaqueteria", "cestas"] },
            // Las notas generales de la obra van en General (petición de BSK, 09/2026); las de
            // liquidación siguen en Costes, junto a la liquidación.
            { id: "notas", label: "Notas de la obra", note: "Seguimiento y comentarios generales de la obra", fields: ["observaciones"], wide: true },
        ],
    },
    {
        id: "hitos",
        label: "Hitos y entregables",
        sections: [
            {
                id: "imagen",
                label: "Imagen, zonificación y legal",
                note: "Concepto de imagen, zonificación, permisos, planos P3 y deadline de mobiliario",
                fields: ["imagen", "imgOriginal", "solZonif", "zonif", "legal", "planoP3", "deadlineMob", "p3Constructora", "p3Mobiliario"],
            },
            {
                id: "mob",
                label: "Mobiliario",
                note: "Proveedor, plazos de gestión y fabricación y montaje en tienda",
                // Rejilla normal (no por zonas): proveedor, gestión y fabricación en una fila y el
                // montaje en la siguiente; con la rejilla de 2 columnas la fabricación quedaba sola.
                fields: ["mobiliario", "bufMobiliario", "fabMobiliario", "inicioMontajeMob", "finMontajeMob"],
            },
            {
                id: "ilum",
                label: "Iluminación y sonido",
                note: "Tipo por zona, proveedores y fecha de entrega a obra",
                fields: ["ilumGral", "ilumCaja", "ilumProb", "ilumVigas", "provIlum1", "provIlum2", "sonido", "fechaIluminacion", "bufIluminacion", "fabIluminacion"],
                rowBreaks: ["provIlum1", "fechaIluminacion"],
            },
            {
                id: "suelos",
                label: "Suelos",
                note: "Proveedor y tipo por zona y fecha de entrega a obra",
                fields: ["sueloGral", "tipoSueloGral", "sueloAlf", "tipoSueloAlf", "sueloProb", "tipoSueloProb", "fechaSuelo", "bufSuelo", "fabSuelo"],
                pairs: [["sueloGral", "tipoSueloGral"], ["sueloAlf", "tipoSueloAlf"], ["sueloProb", "tipoSueloProb"]],
                rowBreaks: ["fechaSuelo"],
            },
        ],
    },
    {
        id: "costes",
        label: "Costes / CAPEX",
        sections: [
            { id: "valoracion", label: "Valoración", note: "CAPEX = coste m² ref. × sup. construida (con dos datos sale el tercero), VNC y ventas", fields: ["cM2", "capex", "vnc", "vta3Year", "ventaM2"] },
            { id: "otros", label: "Otros país / corporativo", note: "Aranceles, servicios generales y tecnología de tienda", fields: ["aranceles", "variosPaisCorp", "ssGg", "techTienda", "totalOtros"] },
            { id: "oc", label: "Obra civil / OC", note: "Licencias, presupuesto, obra, fachada y almacén", fields: ["licencias", "presupuesto", "adicional", "costeObra", "fachada", "almacExt", "totalOc", "euroM2Oc"] },
            { id: "bsk", label: "Equipamiento BSK", note: "Mobiliario, audiovisual, transporte y marked up", fields: ["costeMobiliario", "audiovisual", "variosBsk", "subtotalBsk", "coefTrans", "transporteBsk", "markedUpItx", "totalBsk", "euroM2Bsk"] },
            { id: "control", label: "Inversión y liquidación", note: "Suma de todos los gastos, control automático, liquidación y sus notas", fields: ["totalInversion", "euroM2Inv", "liquidacion", "observacionesCalculo", "liquidacionNotas"], wide: true },
        ],
    },
    {
        // Pestaña propia (petición de Jose, 10/2026): una sola tarjeta a todo el ancho.
        id: "tecnologia",
        label: "Tecnología",
        sections: [
            { id: "tecnologia", label: "Tecnología de tienda", note: "Probadores, cajas, autocobro y dispositivos de tienda", fields: ["numProbadores", "sensorRfidProbador", "overheadProbador", "totalCajaFisica", "totalAcoEstandar", "totalAcoReciclador", "impresoraMovil", "dropOff", "silo", "comentariosTecnologia"], wide: true },
        ],
    },
];

// Campos de texto largo: ocupan más en la tarjeta (cuentan como 3 al emparejar).
export const DETAIL_MULTILINE_FIELDS = new Set(["observacionesCalculo", "liquidacionNotas", "observaciones", "comentariosTecnologia"]);
export const MULTILINE_WEIGHT = 3;

export function detailTabFields(tab) {
    return tab.sections.flatMap((section) => section.fields);
}

export const isZoneSection = (section) => Boolean(section.pairs?.length || section.rowBreaks?.length);

// Filas de una tarjeta: cada fila es una lista de piezas { pair: [a, b] } o { field }.
// Una fila nueva empieza en cada campo de `rowBreaks`. Se omiten los campos que no
// existan según `isKnown`; un par al que le falte un campo se pinta como campo suelto.
export function sectionLayout(section, isKnown = () => true) {
    const pairByFirst = new Map((section.pairs || []).map((pair) => [pair[0], pair]));
    const second = new Set((section.pairs || []).map((pair) => pair[1]));
    const breaks = new Set(section.rowBreaks || []);
    const rows = [[]];
    section.fields.forEach((field) => {
        if (breaks.has(field) && rows[rows.length - 1].length) rows.push([]);
        const row = rows[rows.length - 1];
        if (second.has(field)) return;
        const pair = pairByFirst.get(field);
        if (pair) {
            const known = pair.filter(isKnown);
            if (known.length === 2) row.push({ pair: known });
            else known.forEach((item) => row.push({ field: item }));
            return;
        }
        if (isKnown(field)) row.push({ field });
    });
    return rows.filter((row) => row.length);
}

export function sectionWeight(section) {
    return section.fields.reduce((acc, field) => acc + (DETAIL_MULTILINE_FIELDS.has(field) ? MULTILINE_WEIGHT : 1), 0);
}

// Pestaña y tarjeta de cada campo (buscador de la ficha y saltos a un campo).
const PLACEMENT_BY_FIELD = new Map(DETAIL_TABS.flatMap((tab) => tab.sections.flatMap((section) =>
    section.fields.map((field) => [field, { tabId: tab.id, tabLabel: tab.label, sectionId: section.id, sectionLabel: section.label }]))));
export function detailPlacementOfField(field) {
    return PLACEMENT_BY_FIELD.get(field) || null;
}
export function detailTabOfField(field) {
    return PLACEMENT_BY_FIELD.get(field)?.tabId || null;
}

// Campos de fecha que cuentan como hito con fecha en la línea de datos de la ficha.
export const DETAIL_MILESTONE_DATES = ["zonif", "legal", "planoP3", "deadlineMob", "p3Constructora", "p3Mobiliario", "fechaIluminacion", "fechaSuelo"];

const DAY_MS = 86400000;
const atNoon = (iso) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12) : null;
};

// Datos objetivos bajo el título de la ficha (sin puntuaciones ni estimaciones):
// apertura y días que faltan, CAPEX frente a inversión (misma desviación que la columna
// "Desviación CAPEX" de la hoja) y hitos con fecha.
// formatDate/formatMoney se inyectan para usar los mismos formatos que el resto.
export function buildDetailFacts(obra, today, { formatDate, formatMoney }) {
    const facts = [];
    const opening = atNoon(obra?.apertura);
    if (opening) {
        const base = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12);
        const days = Math.round((opening - base) / DAY_MS);
        const when = days === 0 ? "hoy" : days > 0 ? `en ${days} ${days === 1 ? "día" : "días"}` : `hace ${-days} ${days === -1 ? "día" : "días"}`;
        facts.push({ id: "apertura", label: "Apertura", value: `${formatDate(obra.apertura)} · ${when}`, tone: days < 0 ? "muted" : days <= 30 ? "warning" : "neutral" });
    } else {
        facts.push({ id: "apertura", label: "Apertura", value: "Sin fecha", tone: "muted" });
    }

    const capex = Number(obra?.capex || 0);
    const inversion = Number(obra?.totalInversion || 0);
    if (capex || inversion) {
        const deviation = getCapexDeviation(obra);
        facts.push({
            id: "capex",
            label: "CAPEX / inversión",
            value: `${formatMoney(capex)} / ${formatMoney(inversion)}`,
            detail: `${deviation > 0 ? "▲+" : "▼"}${formatMoney(deviation)}`,
            tone: deviation > 0 ? "danger" : "success",
        });
    }

    const dated = DETAIL_MILESTONE_DATES.filter((field) => atNoon(obra?.[field])).length;
    facts.push({ id: "hitos", label: "Hitos con fecha", value: `${dated}/${DETAIL_MILESTONE_DATES.length}`, tone: dated === DETAIL_MILESTONE_DATES.length ? "success" : "neutral" });
    return facts;
}
